import React, { MouseEvent, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useI18N } from "@src/language/language-context";
import imageIcon from "@src/assets/svg/image-icon.svg";
import {
  acceptStyle,
  rejectStyle,
  stylesDropArea,
} from "../drop-area-add-image/drop-area-add-image-dynamic-styles";
import {
  ACCEPTED_IMAGE_FILE_TYPES,
  addImageFiles,
  ImagePickerErrorMessages,
} from "../image-picker/image-upload-utils";
import styles from "./multiple-drop-area-add-image.module.scss";

interface MultipleDropAreaAddImageProps {
  images: File[];
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  removeLabel: string;
  errorMessages: ImagePickerErrorMessages;
  disabled?: boolean;
}

const cssClassName = "multiple-drop-area-add-image";

export const MultipleDropAreaAddImage: React.FC<
  MultipleDropAreaAddImageProps
> = ({
  images,
  onAdd,
  onRemove,
  removeLabel,
  errorMessages,
  disabled = false,
}) => {
  const i18n = useI18N();
  const texts = {
    attachmentsLabel: i18n("modules.dropAreaAddImage.attachmentsLabel"),
    uploadOrDragAndDropText: i18n(
      "modules.dropAreaAddImage.uploadOrDragAndDropText",
    ),
  };
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    const urls = images.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [images]);

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      accept: ACCEPTED_IMAGE_FILE_TYPES,
      multiple: true,
      disabled,
      onDrop: (acceptedFiles, fileRejections) => {
        const incoming = [
          ...acceptedFiles,
          ...fileRejections.map(({ file }) => file),
        ];
        addImageFiles(incoming, images, onAdd, errorMessages);
      },
    });

  const styleDropArea = useMemo(
    () => ({
      ...stylesDropArea,
      flex: "none",
      minHeight: images.length > 0 ? "134px" : "68px",
      width: "100%",
      gap: "6px",
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [images.length, isDragAccept, isDragReject],
  );

  const handleRemove = (
    event: MouseEvent<HTMLButtonElement>,
    index: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove(index);
  };

  const handleRemoveKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
    }
  };

  return (
    <div className={styles[cssClassName]}>
      <p className={styles[`${cssClassName}__label`]}>
        {texts.attachmentsLabel}
      </p>
      <div
        {...getRootProps({
          className: styles[`${cssClassName}__drop-area`],
          style: styleDropArea,
        })}
      >
        <input {...getInputProps()} />
        <div className={styles[`${cssClassName}__prompt-content`]}>
          <img src={imageIcon} alt="" aria-hidden="true" />
          <p className={styles[`${cssClassName}__prompt`]}>
            {texts.uploadOrDragAndDropText}
          </p>
        </div>
        {previewUrls.length > 0 && (
          <div className={styles[`${cssClassName}__previews`]}>
            {previewUrls.map((url, index) => (
              <div key={url} className={styles[`${cssClassName}__preview`]}>
                <img src={url} alt={images[index]?.name} />
                <button
                  type="button"
                  className={styles[`${cssClassName}__remove`]}
                  aria-label={removeLabel}
                  onClick={(event) => handleRemove(event, index)}
                  onKeyDown={handleRemoveKeyDown}
                  disabled={disabled}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
